import { useMemo, useState } from "react";
import { formatNumber } from "../utils/format";

export interface StackedColumn {
  time: string;
  end: string;
  values: Record<string, number>;
}

export interface UsageStackedData {
  top_models: string[];
  columns: StackedColumn[];
}

interface UsageStackedChartProps {
  data?: UsageStackedData;
  duration: string;
  loading?: boolean;
}

const PALETTE = ["#22C55E", "#60A5FA", "#A78BFA", "#FB923C"];
const OTHERS = "others";
const OTHERS_LABEL = "سایر";
const OTHERS_COLOR = "#94A3B8";
const BAR_HEIGHT = 260;

function formatXLabel(iso: string, duration: string): string {
  const d = new Date(iso);
  if (duration === "1y") return d.toLocaleString("fa-IR", { month: "short" });
  if (duration === "1w")
    return d.toLocaleString("fa-IR", { day: "numeric", month: "short" });
  if (duration === "1mo") return d.toLocaleString("fa-IR", { day: "numeric" });
  return d.toLocaleString("fa-IR", { hour: "2-digit", minute: "2-digit" });
}

function niceMax(max: number): number {
  if (max <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(max)));
  const frac = max / pow;
  let nice = 10;
  if (frac <= 1) nice = 1;
  else if (frac <= 2) nice = 2;
  else if (frac <= 2.5) nice = 2.5;
  else if (frac <= 5) nice = 5;
  return nice * pow;
}

export default function UsageStackedChart({ data, duration, loading }: UsageStackedChartProps) {
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [hoveredLegend, setHoveredLegend] = useState<string | null>(null);
  const [hoveredColumn, setHoveredColumn] = useState<number | null>(null);

  const columns = data?.columns || [];
  const baseSeries = useMemo(() => [...(data?.top_models || []), OTHERS], [data]);
  const colorFor = useMemo(() => {
    const map = new Map<string, string>();
    baseSeries.forEach((s, i) => {
      if (s === OTHERS) map.set(s, OTHERS_COLOR);
      else map.set(s, PALETTE[i % PALETTE.length]);
    });
    return map;
  }, [baseSeries]);
  const visibleSeries = baseSeries.filter((s) => !hidden.has(s));

  const toggleSeries = (name: string) => {
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const { totals, maxToman, dataGrandTotal } = useMemo(() => {
    const totals = columns.map((col) =>
      visibleSeries.reduce((sum, s) => sum + (Number(col.values[s]) || 0), 0)
    );
    const dataGrandTotal = columns.reduce(
      (sum, col) => sum + baseSeries.reduce((s, name) => s + (Number(col.values[name]) || 0), 0),
      0
    );
    return { totals, maxToman: niceMax(Math.max(0, ...totals) / 10), dataGrandTotal };
  }, [columns, visibleSeries, baseSeries]);

  if (loading) {
    return (
      <div className="h-[300px] flex items-center justify-center">
        <div className="h-44 w-full bg-surface-hover rounded animate-pulse" />
      </div>
    );
  }

  if (!columns.length || dataGrandTotal <= 0) {
    return (
      <div className="h-[260px] flex items-center justify-center text-text-tertiary">
        <p className="text-body">داده‌ای برای نمایش وجود ندارد</p>
      </div>
    );
  }

  const ticks = [0, 1, 2, 3, 4].map((i) => (maxToman * i) / 4);
  const tooltipLeft =
    hoveredColumn != null
      ? Math.min(Math.max(((hoveredColumn + 0.5) / columns.length) * 100, 10), 90)
      : 0;
  const hoveredEntries =
    hoveredColumn != null
      ? visibleSeries
          .map((s) => ({
            name: s,
            color: colorFor.get(s) || "#999",
            irr: Number(columns[hoveredColumn].values[s]) || 0,
          }))
          .filter((e) => e.irr > 0)
          .map((e) => ({ ...e, toman: Math.round(e.irr / 10) }))
      : [];
  const totalToman = hoveredEntries.reduce((a, e) => a + e.toman, 0);

  return (
    <div>
      <div className="flex gap-3" dir="ltr">
        {/* Y axis */}
        <div className="relative w-16 shrink-0" style={{ height: BAR_HEIGHT }}>
          {ticks.map((t) => (
            <span
              key={t}
              dir="rtl"
              className="absolute right-1 -translate-y-1/2 text-[10px] text-text-tertiary"
              style={{ top: `${(1 - t / maxToman) * 100}%` }}
            >
              {formatNumber(t)}
            </span>
          ))}
        </div>

        {/* Chart area */}
        <div className="relative flex-1" style={{ height: BAR_HEIGHT }}>
          {ticks.map((t) => (
            <div
              key={t}
              className="absolute left-0 right-0"
              style={{ top: `${(1 - t / maxToman) * 100}%`, height: 1, background: "rgb(var(--color-border))" }}
            />
          ))}

          {hoveredColumn != null && (
            <div
              dir="rtl"
              className="absolute z-30 top-0 -translate-x-1/2"
              style={{ left: `${tooltipLeft}%` }}
            >
              <div
                className="px-3 py-2.5 rounded-btn whitespace-nowrap"
                style={{
                  background: "rgba(26,26,26,0.92)",
                  color: "#fff",
                  boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                }}
              >
                {hoveredEntries.map((e) => (
                  <div key={e.name} className="flex items-center justify-between gap-5 py-0.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: e.color }} />
                      <span dir="ltr" className="text-[11px] truncate max-w-[170px]">
                        {e.name === OTHERS ? OTHERS_LABEL : e.name}
                      </span>
                    </div>
                    <span className="text-xs font-medium" style={{ color: "rgb(var(--chart-green))" }}>
                      {formatNumber(e.toman)}
                    </span>
                  </div>
                ))}
                <div className="my-1 h-px" style={{ background: "rgba(255,255,255,0.15)" }} />
                <div className="flex items-center justify-between gap-5">
                  <span className="text-[11px]" style={{ opacity: 0.8 }}>
                    مجموع
                  </span>
                  <span className="text-xs font-bold" style={{ color: "rgb(var(--chart-green))" }}>
                    {formatNumber(totalToman)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Bars */}
          <div className="absolute inset-0 flex items-end gap-1">
            {columns.map((col, i) => (
              <div
                key={i}
                className="relative flex-1 h-full flex flex-col justify-end"
                onMouseEnter={() => setHoveredColumn(i)}
                onMouseLeave={() => setHoveredColumn(null)}
              >
                {visibleSeries.map((s) => {
                  const irr = Number(col.values[s]) || 0;
                  const pct = maxToman > 0 ? (irr / 10 / maxToman) * 100 : 0;
                  const dimmed = hoveredLegend != null && s !== hoveredLegend;
                  return (
                    <div
                      key={s}
                      className="w-full"
                      style={{
                        height: `${pct}%`,
                        background: colorFor.get(s),
                        opacity: dimmed ? 0.25 : 1,
                        transition: "opacity 150ms",
                      }}
                    />
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* X labels */}
      <div className="flex gap-1 ml-[76px]" dir="ltr">
        {columns.map((col, i) => (
          <div
            key={i}
            dir="rtl"
            className="flex-1 text-center text-[10px] text-text-tertiary truncate"
          >
            {formatXLabel(col.time, duration)}
          </div>
        ))}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mt-5">
        {baseSeries.map((s) => {
          const isHidden = hidden.has(s);
          const isHovered = hoveredLegend === s;
          const dimmed = hoveredLegend != null && !isHovered;
          return (
            <button
              key={s}
              type="button"
              onClick={() => toggleSeries(s)}
              onMouseEnter={() => setHoveredLegend(s)}
              onMouseLeave={() => setHoveredLegend(null)}
              className={`flex items-center gap-2 text-body text-text-main transition-opacity ${
                isHidden ? "line-through opacity-40" : ""
              } ${isHovered ? "font-bold" : "font-medium"}`}
              style={{ opacity: dimmed ? 0.4 : undefined }}
            >
              <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: colorFor.get(s) }} />
              <span dir="ltr">{s === OTHERS ? OTHERS_LABEL : s}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
